/**
 * NewProjectDialog — small modal for creating a project directly from
 * the dashboard (outside the New Meeting form's auto-create flow).
 *
 * Projects are still auto-created when a user types a new name in the
 * New Meeting form, but some users prefer explicit control — this
 * dialog lets them pre-register a project so it shows up in the
 * dashboard + typeahead before any meeting has been captured for it.
 *
 * Server side: POST /projects is idempotent on case-insensitive name
 * match, so double-submits / name collisions return the existing row
 * rather than erroring.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/apiClient.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';
import { Modal, ModalHeader, ModalBody } from './ui/Modal.tsx';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (project: { id: string; name: string }) => void;
}

export function NewProjectDialog({ isOpen, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const qc = useQueryClient();

  // Reset the input every time the dialog opens so a prior half-typed
  // value doesn't stick around across sessions.
  useEffect(() => {
    if (isOpen) setName('');
  }, [isOpen]);

  const create = useMutation({
    mutationFn: (projectName: string) =>
      api.post<{ id: string; name: string }>('/projects', { name: projectName }),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects-with-counts'] });
      onCreated?.(project);
      onClose();
    },
  });

  if (!isOpen) return null;

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !create.isPending;
  const errMsg =
    create.error instanceof ApiError
      ? create.error.message
      : create.error instanceof Error
        ? create.error.message
        : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      ariaLabel="Create a new project"
    >
      <ModalHeader
        title="New project"
        subtitle="Pre-register a project so it shows up in the dashboard before you capture any meetings for it."
        onClose={onClose}
      />

      <ModalBody>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate(trimmed);
          }}
        >
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
              Project name
            </span>
            <input
              {...NO_AUTOFILL}
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Duke Park Master Plan"
              className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
              maxLength={200}
            />
          </label>

          {errMsg && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {errMsg}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {create.isPending ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}
